import React from "react";

const RoutePortalTargetIdContext = React.createContext<string | null>(null);

type RoutePortalProviderProps = {
  targetId: string;
  children: React.ReactNode;
};

/**
 * Binds body-style presentation (menus/modals/lightboxes) to the route surface
 * that owns it. The target is resolved in a layout effect so the first visible
 * paint can never fall back to document.body while the route host is mounting.
 */
export const RoutePortalProvider: React.FC<RoutePortalProviderProps> = ({
  targetId,
  children,
}) => (
  <RoutePortalTargetIdContext.Provider value={targetId}>
    {children}
  </RoutePortalTargetIdContext.Provider>
);

/**
 * Returns the current route's portal host. Outside an app route provider we
 * intentionally fall back to document.body so shared dialogs still work on
 * standalone/public surfaces.
 */
export function useRoutePortalTarget(): HTMLElement | null {
  const targetId = React.useContext(RoutePortalTargetIdContext);
  const [target, setTarget] = React.useState<HTMLElement | null>(() => {
    if (typeof document === "undefined") return null;
    if (!targetId) return document.body;
    return document.getElementById(targetId);
  });

  React.useLayoutEffect(() => {
    if (typeof document === "undefined") {
      setTarget(null);
      return;
    }

    setTarget(targetId ? document.getElementById(targetId) : document.body);
  }, [targetId]);

  return target;
}
